import React, { useEffect, useState } from 'react';
import MainLayout from '@/components/Layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, Receipt, Calendar, DollarSign } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const BillVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const billNumber = searchParams.get('bill');
  const [verified, setVerified] = useState<boolean | null>(null);

  useEffect(() => {
    document.title = 'Bill Verification - Growth Energy';
    
    // Simulate verification check
    if (billNumber) {
      setTimeout(() => setVerified(true), 500);
    } else {
      setVerified(false);
    }
  }, [billNumber]);

  return (
    <MainLayout title="Bill Verification">
      <div className="p-4 max-w-2xl mx-auto space-y-6">
        <div className="bg-gradient-to-r from-blue-500/10 to-blue-500/5 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-2">
            <Receipt className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-semibold text-foreground">Bill Verification</h2>
          </div>
          <p className="text-muted-foreground">Verify the authenticity of your electricity bill</p>
        </div>

        {verified === null ? (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            </CardContent>
          </Card>
        ) : verified ? (
          <Card className="border-green-200 dark:border-green-800">
            <CardHeader className="bg-green-50 dark:bg-green-950/50">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                <CardTitle className="text-green-900 dark:text-green-100">Bill Verified</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-3">
                <div className="flex items-start space-x-3 p-3 bg-muted/50 rounded-lg">
                  <Receipt className="w-5 h-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Bill Number</p>
                    <p className="font-semibold text-foreground">{billNumber}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 bg-muted/50 rounded-lg">
                  <Calendar className="w-5 h-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Verified On</p>
                    <p className="font-semibold text-foreground">{new Date().toLocaleDateString()}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 bg-muted/50 rounded-lg">
                  <DollarSign className="w-5 h-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Status</p>
                    <p className="font-semibold text-green-600 dark:text-green-400">Authentic Bill</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  This is an authentic electricity bill issued by Growth Energy. Please ensure timely payment to avoid service interruption.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-red-200 dark:border-red-800">
            <CardHeader className="bg-red-50 dark:bg-red-950/50">
              <div className="flex items-center space-x-3">
                <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
                <CardTitle className="text-red-900 dark:text-red-100">Verification Failed</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-muted-foreground">
                Unable to verify this bill. The QR code may be invalid or the bill number is missing.
              </p>
              <p className="text-sm text-muted-foreground mt-4">
                Please contact Growth Energy support if you believe this is an error.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default BillVerification;
